import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Add, Assignment, Edit } from '@mui/icons-material';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import { eventApi, fetcher } from 'lib/api';
import { Permissions } from 'config';
import styles from './EventsList.module.scss';

const formatWhen = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
};

const statusKey = (status) => {
    if (status === 'hidden') return 'events.hidden_in_app';
    if (status === 'past') return 'events.past';
    if (status === 'registration_closed') return 'events.registration_closed';
    return 'events.visible_in_app';
};

const EventsList = ({ t }) => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const canCreate = (user.permission || []).includes(Permissions.EVENT_CREATE);
    const canUpdate = (user.permission || []).includes(Permissions.EVENT_UPDATE);
    const canDelete = (user.permission || []).includes(Permissions.EVENT_DELETE);
    const { data, mutate, isLoading } = useSWR(eventApi.list(), fetcher);
    const events = data?.results || [];
    const [dialog, setDialog] = useState({ open: false, event: null });

    const handleDelete = async () => {
        if (!dialog.event) return;
        await fetcher(eventApi.delete(dialog.event.id), 'DELETE');
        setDialog({ open: false, event: null });
        mutate();
    };

    return (
        <div className={styles.page}>
            <SparaTitle
                title={t('menu.events')}
                rightComponent={
                    canCreate ? (
                        <SparaButton icon={Add} onClick={() => navigate('/events/new')}>
                            {t('events.new')}
                        </SparaButton>
                    ) : null
                }
            />
            {isLoading ? <p>{t('events.loading')}</p> : null}
            {!isLoading && events.length === 0 ? (
                <p className={styles.empty}>{t('events.empty')}</p>
            ) : (
                <div className={styles.list}>
                    {events.map((event) => (
                        <div key={event.id} className={styles.card}>
                            <div className={styles.thumb}>
                                {event.image ? (
                                    <img src={event.image} alt="" />
                                ) : (
                                    <div className={styles.thumbPlaceholder} />
                                )}
                            </div>
                            <div className={styles.body}>
                                <Link to={`/events/${event.id}`} className={styles.title}>
                                    {t('events.list_title', {
                                        number: event.number,
                                        title: event.title,
                                    })}
                                </Link>
                                <p className={styles.meta}>
                                    {formatWhen(event.starts_at)}
                                    {event.location ? ` · ${event.location}` : ''}
                                </p>
                                <p className={styles.counts}>
                                    {t('events.registered_of_capacity', {
                                        registered: event.registered_count ?? 0,
                                        capacity: event.capacity ?? t('events.unlimited'),
                                    })}
                                    {' · '}
                                    {t('events.attended_count', { count: event.attended_count ?? 0 })}
                                </p>
                                <p
                                    className={`${styles.status} ${
                                        event.app_status === 'visible' ? styles.statusVisible : styles.statusMuted
                                    }`}
                                >
                                    {t(statusKey(event.app_status))}
                                </p>
                            </div>
                            <div className={styles.actions}>
                                <SparaButton
                                    variant="outlined"
                                    size="small"
                                    icon={Assignment}
                                    onClick={() => navigate(`/events/${event.id}/responses`)}
                                >
                                    {t('events.responses')}
                                </SparaButton>
                                {canUpdate ? (
                                    <SparaButton
                                        variant="outlined"
                                        size="small"
                                        icon={Edit}
                                        onClick={() => navigate(`/events/${event.id}`)}
                                    >
                                        {t('edit')}
                                    </SparaButton>
                                ) : null}
                                {canDelete ? (
                                    <SparaButton
                                        variant="outlined"
                                        color="danger"
                                        size="small"
                                        onClick={() => setDialog({ open: true, event })}
                                    >
                                        {t('delete')}
                                    </SparaButton>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <ConfirmDialog
                open={dialog.open}
                title={t('events.delete_title')}
                message={t('events.delete_message')}
                onConfirm={handleDelete}
                onCancel={() => setDialog({ open: false, event: null })}
            />
        </div>
    );
};

export default withTranslation()(EventsList);
